import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {
  revokeDeviceAccount,
  setDeviceAccountPassword,
} from '@/entities/device/api';
import type {DeviceAccount, DeviceSummary} from '@/entities/device/types';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {AppInput} from '@/shared/ui/input';
import {ListRow, RowGroup} from '@/shared/ui/list-row';
import {Sheet} from '@/shared/ui/sheet';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {spacing} from '@/shared/ui/theme';
import {formatTimestamp} from '@/shared/lib/format';

/**
 * The terminal's own accounts — who can open its menu, and with which role.
 *
 * The board owns this list; the cloud is a mirror with exactly two powers:
 * change a password and revoke. Creating an account and changing a role both
 * happen at the terminal, in front of the person concerned, and the card says
 * so instead of leaving the owner hunting for a button that is not there.
 *
 * Both actions are QUEUED commands: they land on the board's next sync, not on
 * the response. The success line says that rather than claiming "done".
 */
const MIN_PASSWORD = 8;

const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  card: {gap: spacing(3)},
  sheetBody: {gap: spacing(3), paddingBottom: spacing(4)},
});

/** Fewer than two admins means the board will refuse to drop this one. */
function isLastAdmin(accounts: DeviceAccount[], account: DeviceAccount): boolean {
  if (account.role !== 'admin') {
    return false;
  }
  return accounts.filter(a => a.role === 'admin').length <= 1;
}

export function DeviceAccountsCard({
  companyId,
  deviceId,
  summary,
}: {
  companyId: string;
  deviceId: string;
  summary: DeviceSummary;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const key = ['device', companyId, deviceId];

  const accounts = summary.access_accounts ?? [];

  const [editing, setEditing] = useState<DeviceAccount | null>(null);
  const [password, setPassword] = useState('');
  const [revoking, setRevoking] = useState<DeviceAccount | null>(null);

  const closeSheets = () => {
    setEditing(null);
    setRevoking(null);
    // The typed password never outlives the sheet — not in state, not in a log.
    setPassword('');
  };

  const setPasswordMutation = useMutation({
    mutationFn: (input: {username: string; password: string}) =>
      setDeviceAccountPassword(companyId, deviceId, input.username, input.password),
    onSuccess: closeSheets,
    onSettled: () => {
      void qc.invalidateQueries({queryKey: key});
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (username: string) =>
      revokeDeviceAccount(companyId, deviceId, username),
    onSuccess: closeSheets,
    onSettled: () => {
      void qc.invalidateQueries({queryKey: key});
    },
  });

  // Absence is not emptiness. `access_accounts` is a repeated field, so an old
  // firmware and a board with no accounts both arrive as []; `access_activated`
  // is the field that distinguishes them, and reading silence as "no accounts"
  // would tell the owner their terminal is unprotected.
  if (summary.access_activated === undefined || summary.access_activated === null) {
    return (
      <Card padding={4} style={styles.card}>
        <AppText variant="caption">{t('terminal.accounts.notReported')}</AppText>
      </Card>
    );
  }

  return (
    <View style={styles.root}>
      {summary.access_menu_mode === 'legacy_codes' ? (
        <Banner
          tone="error"
          title={t('terminal.accounts.legacyTitle')}
          message={t('terminal.accounts.legacyBody')}
        />
      ) : null}

      <Card padding={4} style={styles.card}>
        <AppText variant="caption">{t('terminal.accounts.hint')}</AppText>

        {accounts.length === 0 ? (
          <AppText variant="caption">{t('terminal.accounts.empty')}</AppText>
        ) : (
          <RowGroup>
            {accounts.map(a => (
              <ListRow
                key={a.username}
                title={a.username}
                subtitle={
                  a.updated_at_millis > 0
                    ? t('terminal.accounts.changedAt', {
                        at: formatTimestamp(new Date(a.updated_at_millis).toISOString()),
                      })
                    : undefined
                }
                chevron={false}
                trailing={
                  <StatusPill
                    tone={a.role === 'admin' ? 'online' : 'neutral'}
                    size="sm"
                    dot={false}
                    label={t(`terminal.accounts.role.${a.role}`, {
                      defaultValue: a.role,
                    })}
                  />
                }
                onPress={() => {
                  setPassword('');
                  setEditing(a);
                }}
              />
            ))}
          </RowGroup>
        )}
      </Card>

      <Sheet
        visible={!!editing}
        onClose={closeSheets}
        title={editing?.username}
        subtitle={t('terminal.accounts.sheetHint')}
        testID="device-account-sheet">
        <View style={styles.sheetBody}>
          <AppInput
            label={t('terminal.accounts.newPassword')}
            value={password}
            onChangeText={setPassword}
            secure
            autoCapitalize="none"
            autoCorrect={false}
            showPasswordLabel={t('a11y.showPassword')}
            hidePasswordLabel={t('a11y.hidePassword')}
            hint={t('terminal.accounts.passwordHint', {min: MIN_PASSWORD})}
            testID="device-account-password"
          />
          <AppButton
            title={t('terminal.accounts.savePassword')}
            disabled={
              password.length < MIN_PASSWORD || setPasswordMutation.isPending
            }
            loading={setPasswordMutation.isPending}
            onPress={() => {
              if (editing) {
                setPasswordMutation.mutate({
                  username: editing.username,
                  password,
                });
              }
            }}
          />
          {setPasswordMutation.isError ? (
            <Banner tone="error" title={t('terminal.accounts.passwordError')} />
          ) : null}

          {editing && isLastAdmin(accounts, editing) ? (
            // Greyed with the reason next to it. This is a hint, not a guard —
            // the board refuses either way, and a button that fails silently
            // teaches the owner nothing.
            <>
              <AppButton
                title={t('terminal.accounts.revoke')}
                tone="ghost"
                disabled
                onPress={() => {}}
              />
              <AppText variant="caption">
                {t('terminal.accounts.lastAdmin')}
              </AppText>
            </>
          ) : (
            <AppButton
              title={t('terminal.accounts.revoke')}
              tone="ghost"
              onPress={() => {
                const target = editing;
                setEditing(null);
                setPassword('');
                setRevoking(target);
              }}
            />
          )}

          <AppText variant="caption">{t('terminal.accounts.cannotCreate')}</AppText>
          <AppText variant="caption">{t('terminal.accounts.cannotChangeRole')}</AppText>
        </View>
      </Sheet>

      <Sheet
        visible={!!revoking}
        onClose={closeSheets}
        title={t('terminal.accounts.revokeTitle')}
        subtitle={t('terminal.accounts.revokeBody', {name: revoking?.username ?? ''})}
        testID="device-account-revoke-sheet">
        <View style={styles.sheetBody}>
          <AppButton
            title={t('terminal.accounts.revoke')}
            tone="destructive"
            loading={revokeMutation.isPending}
            disabled={revokeMutation.isPending}
            onPress={() => {
              if (revoking) {
                revokeMutation.mutate(revoking.username);
              }
            }}
          />
          <AppButton
            title={t('actions.cancel')}
            tone="ghost"
            onPress={closeSheets}
          />
          {revokeMutation.isError ? (
            <Banner tone="error" title={t('terminal.accounts.revokeError')} />
          ) : null}
        </View>
      </Sheet>

      {setPasswordMutation.isSuccess || revokeMutation.isSuccess ? (
        <Banner tone="success" title={t('terminal.accounts.queued')} />
      ) : null}
    </View>
  );
}
